import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { PAGINATION_MODES } from "@hyro-components/hyro-table/HyroTable.consts";
import { useServerPagination } from "./hooks/ServerPagination.hook";
import { useClientPagination } from "./hooks/ClientPagination.hook";
import { hasActiveFilters } from "@utils";

const EMPTY_ARRAY = [];

export const useHyroTableContainer = ({
  paginationMode,
  filters: initialFilters = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  enableBulkEdit,
  selectedRows,
  setSelectedRows,
  handlePaginationParamsChange,
  onFiltersChange,
  searchQueryParam,
  searchFields
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowLengthChanged, setRowLengthChanged] = useState(false);
  const loggedDuplicateIdsRef = useRef(new Set());
  const isServerMode = paginationMode === PAGINATION_MODES.SERVER;
  const serverPagination = useServerPagination({ initialFilters, data, handlePaginationParamsChange });
  const clientPagination = useClientPagination({ initialFilters, data, onFiltersChange, searchFields });
  const currentHook = isServerMode ? serverPagination : clientPagination;
  const isFilteringOn = currentHook.searchText || hasActiveFilters(currentHook.filters);

  const handleSearch = (value) => {
    currentHook.setSearchText(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(searchQueryParam, value);
    } else {
      newParams.delete(searchQueryParam);
    }
    if (!searchQueryParam) return;
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (!enableBulkEdit || !rowLengthChanged || selectedRows?.length === 0) return;

    if (selectedRows.length + 1 === currentHook.rows.length) {
      setSelectedRows?.(currentHook.rows.map(row => row.id));
    } else if (selectedRows.length - 1 === currentHook.rows.length) {
      setSelectedRows?.(currentHook.rows.map(row => row.id));
    }

    setRowLengthChanged(false);
  }, [currentHook.rows, rowLengthChanged, selectedRows, setSelectedRows, enableBulkEdit]);

  useEffect(() => {
    if (!searchQueryParam) return;
    const search = searchParams.get(searchQueryParam);
    if (search) {
      currentHook.setSearchText(search);
    }
  }, [searchParams, searchQueryParam]);

  // Filter out duplicate rows by their id
  const filteredRows = useMemo(() => {
    const seenIds = new Set();
    return currentHook.rows.filter(row => {
      const { id } = row;
      if (seenIds.has(id)) {
        // Log once per id per mount — this memo recomputes on every search/sort/rows change
        if (!loggedDuplicateIdsRef.current.has(id)) {
          loggedDuplicateIdsRef.current.add(id);
          console.error("Duplicate row ID detected in table data.", {
            duplicateRowId: id,
            pathname: window.location.pathname
          });
        }
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }, [currentHook.rows]);

  return {
    ...currentHook,
    rows: filteredRows,
    isFilteringOn,
    rowLengthChanged,
    setRowLengthChanged,
    handleSearch
  };
};
